import { useEffect, useState, useMemo, FormEvent } from 'react';
import {
  adminFetchProducts, adminUpdateProduct, Product,
  adminFetchOrders, adminUpdateOrderStatus, adminDeleteOrder, adminCreateManualOrder, Order, OrdersSummary,
} from '@/lib/api';

const ORDER_STATUS_OPTIONS = ['pending', 'paid', 'shipped', 'cancelled', 'failed', 'paid_stock_issue'];

const STORAGE_KEY = 'roff_admin_pw';

const STATUS_LABELS: Record<string, string> = {
  pending: '未決済',
  paid: '決済済み',
  shipped: '発送済み',
  cancelled: 'キャンセル',
  failed: '決済失敗',
  paid_stock_issue: '要確認(在庫不整合)',
};

const PAYMENT_PROVIDER_LABELS: Record<string, string> = {
  univapay: 'クレジットカード',
  bank_transfer: '銀行振込',
  hand_delivery: '手渡し・その他',
};

const SHIPPING_METHOD_LABELS: Record<string, string> = {
  yamato80: 'ヤマト',
  yamato60: 'ヤマト',
  clickpost: '日本郵便',
};

type EditState = {
  price: string;
  description: string;
  category: string;
  stock: string;
  published: boolean;
};

function toEditState(p: Product): EditState {
  return {
    price: String(p.price),
    description: p.description,
    category: p.category,
    stock: String(p.stock),
    published: p.published,
  };
}

export default function AdminPage() {
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<'products' | 'orders'>('products');

  const [products, setProducts] = useState<Product[]>([]);
  const [edits, setEdits] = useState<Record<string, EditState>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, Partial<Record<keyof EditState, true>>>>({});
  const [savingSlug, setSavingSlug] = useState<string | null>(null);
  const [rowMessage, setRowMessage] = useState<Record<string, string>>({});
  const [listError, setListError] = useState('');

  const [orders, setOrders] = useState<Order[]>([]);
  const [summary, setSummary] = useState<OrdersSummary | null>(null);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);
  const [statusSaving, setStatusSaving] = useState<number | null>(null);
  const [deletingOrder, setDeletingOrder] = useState<number | null>(null);
  const [trackingInputs, setTrackingInputs] = useState<Record<number, string>>({});

  const [showManualForm, setShowManualForm] = useState(false);
  const [showMonthlyStats, setShowMonthlyStats] = useState(false);
  const [manualQuantities, setManualQuantities] = useState<Record<string, string>>({});
  const [manualPaymentMethod, setManualPaymentMethod] = useState('現金');
  const [manualCustomerName, setManualCustomerName] = useState('');
  const [manualNotes, setManualNotes] = useState('');
  const [manualSaving, setManualSaving] = useState(false);
  const [manualError, setManualError] = useState('');
  const [manualSuccess, setManualSuccess] = useState('');

  const loadProducts = async (pw: string) => {
    setLoading(true);
    setListError('');
    try {
      const list = await adminFetchProducts(pw);
      setProducts(list);
      setEdits(Object.fromEntries(list.map(p => [p.slug, toEditState(p)])));
      setAuthed(true);
      sessionStorage.setItem(STORAGE_KEY, pw);
    } catch (err) {
      if (err instanceof Error && err.message === 'unauthorized') {
        setLoginError('パスワードが違います');
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
      } else {
        setListError('商品情報の取得に失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async (pw: string) => {
    setOrdersError('');
    try {
      const data = await adminFetchOrders(pw);
      setOrders(data.orders);
      setSummary(data.summary);
      setOrdersLoaded(true);
    } catch {
      setOrdersError('注文情報の取得に失敗しました');
    }
  };

  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      setPassword(saved);
      loadProducts(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authed && !ordersLoaded) {
      loadOrders(password);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const handleLogin = (e: FormEvent) => {
    e.preventDefault();
    setLoginError('');
    loadProducts(password);
  };

  const handleFieldChange = (slug: string, field: keyof EditState, value: string | boolean) => {
    setEdits(prev => ({ ...prev, [slug]: { ...prev[slug], [field]: value } }));
    setTouchedFields(prev => ({ ...prev, [slug]: { ...prev[slug], [field]: true } }));
  };

  const handleSave = async (slug: string) => {
    const edit = edits[slug];
    const touched = touchedFields[slug];
    if (!edit || !touched) return;
    setSavingSlug(slug);
    setRowMessage(prev => ({ ...prev, [slug]: '' }));
    try {
      // 実際に編集した項目だけ送る(触ってない項目、特に在庫は他の注文で変わっている可能性があるため上書きしない)
      const updates: Parameters<typeof adminUpdateProduct>[2] = {};
      if (touched.price) updates.price = Number(edit.price);
      if (touched.description) updates.description = edit.description;
      if (touched.category) updates.category = edit.category;
      if (touched.stock) updates.stock = Number(edit.stock);
      if (touched.published) updates.published = edit.published;

      await adminUpdateProduct(password, slug, updates);
      setTouchedFields(prev => ({ ...prev, [slug]: {} }));
      setRowMessage(prev => ({ ...prev, [slug]: '保存しました' }));
    } catch {
      setRowMessage(prev => ({ ...prev, [slug]: '保存に失敗しました' }));
    } finally {
      setSavingSlug(null);
    }
  };

  const handleStatusChange = async (orderId: number, status: string, order?: Order) => {
    const trackingNumber = trackingInputs[orderId] ?? order?.trackingNumber ?? '';
    if (status === 'shipped' && !trackingNumber.trim()) {
      if (!window.confirm('追跡番号が未入力です。追跡番号なしで発送済みメールを送信しますか?')) return;
    }
    setStatusSaving(orderId);
    try {
      await adminUpdateOrderStatus(password, orderId, status, trackingNumber || undefined);
      setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, status, trackingNumber: trackingNumber || o.trackingNumber } : o)));
    } catch {
      setOrdersError('ステータスの更新に失敗しました');
    } finally {
      setStatusSaving(null);
    }
  };

  const handleDeleteOrder = async (order: Order) => {
    if (!window.confirm(`注文 ${order.orderNumber}(¥${order.total.toLocaleString()})を削除します。この操作は取り消せません。よろしいですか?`)) {
      return;
    }
    setDeletingOrder(order.id);
    try {
      await adminDeleteOrder(password, order.id);
      setOrders(prev => prev.filter(o => o.id !== order.id));
      setSummary(prev => prev && {
        totalOrders: prev.totalOrders - 1,
        paidOrders: order.status === 'paid' ? prev.paidOrders - 1 : prev.paidOrders,
        totalRevenue: order.status === 'paid' ? prev.totalRevenue - order.total : prev.totalRevenue,
      });
    } catch {
      setOrdersError('削除に失敗しました');
    } finally {
      setDeletingOrder(null);
    }
  };

  const handleManualQuantityChange = (slug: string, value: string) => {
    setManualQuantities(prev => ({ ...prev, [slug]: value }));
  };

  const handleManualSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const items = Object.entries(manualQuantities)
      .map(([slug, qty]) => ({ slug, quantity: Number(qty) }))
      .filter(i => i.quantity > 0);

    if (items.length === 0) {
      setManualError('商品と数量を入力してください');
      return;
    }

    setManualSaving(true);
    setManualError('');
    setManualSuccess('');
    try {
      const result = await adminCreateManualOrder(password, {
        items,
        paymentMethod: manualPaymentMethod,
        customerName: manualCustomerName || undefined,
        notes: manualNotes || undefined,
      });
      if (result.error) throw new Error(result.error);
      setManualSuccess(`登録しました(${result.orderNumber})`);
      setManualQuantities({});
      setManualCustomerName('');
      setManualNotes('');
      setOrdersLoaded(false);
      loadOrders(password);
      loadProducts(password);
    } catch (err) {
      setManualError(err instanceof Error ? err.message : '登録に失敗しました');
    } finally {
      setManualSaving(false);
    }
  };

  type MonthlyStat = {
    month: string; // 'YYYY-MM'
    totalRevenue: number;
    orderCount: number;
    products: { name: string; quantity: number; revenue: number }[];
  };

  const monthlyStats = useMemo<MonthlyStat[]>(() => {
    const byMonth = new Map<string, { totalRevenue: number; orderCount: number; products: Map<string, { quantity: number; revenue: number }> }>();

    for (const o of orders) {
      if (o.status !== 'paid' && o.status !== 'shipped' && o.status !== 'paid_stock_issue') continue;
      const dateStr = o.paidAt ?? o.createdAt;
      const month = dateStr.slice(0, 7); // 'YYYY-MM'
      if (!byMonth.has(month)) {
        byMonth.set(month, { totalRevenue: 0, orderCount: 0, products: new Map() });
      }
      const bucket = byMonth.get(month)!;
      bucket.totalRevenue += o.total;
      bucket.orderCount += 1;
      for (const item of o.items) {
        const existing = bucket.products.get(item.name) ?? { quantity: 0, revenue: 0 };
        existing.quantity += item.quantity;
        existing.revenue += item.subtotal;
        bucket.products.set(item.name, existing);
      }
    }

    return Array.from(byMonth.entries())
      .map(([month, data]) => ({
        month,
        totalRevenue: data.totalRevenue,
        orderCount: data.orderCount,
        products: Array.from(data.products.entries())
          .map(([name, v]) => ({ name, quantity: v.quantity, revenue: v.revenue }))
          .sort((a, b) => b.revenue - a.revenue),
      }))
      .sort((a, b) => (a.month < b.month ? 1 : -1));
  }, [orders]);

  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const activeMonth = selectedMonth || monthlyStats[0]?.month || '';
  const activeMonthStat = monthlyStats.find(m => m.month === activeMonth) ?? null;

  // 累計販売数(キャンセル・決済失敗の注文は除く)
  const soldCountByName = useMemo(() => {
    const counts = new Map<string, number>();
    for (const o of orders) {
      if (o.status === 'cancelled' || o.status === 'failed') continue;
      for (const item of o.items) {
        counts.set(item.name, (counts.get(item.name) ?? 0) + item.quantity);
      }
    }
    return counts;
  }, [orders]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-6">
        <form onSubmit={handleLogin} className="w-full max-w-sm bg-white border border-background-200 rounded-xl p-8">
          <h1 className="text-lg text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
            ROFF Goods 管理画面
          </h1>
          <label className="block text-xs text-foreground-700 mb-2 tracking-wider">パスワード</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="w-full border border-background-300 px-4 py-3 text-sm outline-none focus:border-primary-400 rounded-lg mb-4"
            autoFocus
          />
          {loginError && <p className="text-xs text-primary-600 mb-4">{loginError}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-500 text-white py-3 text-sm rounded-full disabled:opacity-50"
          >
            {loading ? '確認中...' : 'ログイン'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 px-6 py-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
            ROFF Goods 管理画面
          </h1>
          <button
            onClick={() => {
              sessionStorage.removeItem(STORAGE_KEY);
              setAuthed(false);
              setPassword('');
            }}
            className="text-xs text-foreground-500 hover:text-primary-500"
          >
            ログアウト
          </button>
        </div>

        <div className="flex gap-2 mb-8">
          <button
            onClick={() => setTab('products')}
            className={`px-5 py-2 text-xs tracking-wider rounded-full ${tab === 'products' ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600'}`}
          >
            商品管理
          </button>
          <button
            onClick={() => setTab('orders')}
            className={`px-5 py-2 text-xs tracking-wider rounded-full ${tab === 'orders' ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600'}`}
          >
            売上管理
          </button>
        </div>

        {tab === 'products' && (
          <>
            {listError && <p className="text-sm text-primary-600 mb-4">{listError}</p>}
            <div className="space-y-4">
              {products.map(p => {
                const edit = edits[p.slug];
                if (!edit) return null;
                return (
                  <div key={p.slug} className="bg-white border border-background-200 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>{p.name}</p>
                        <p className="text-xs text-foreground-400">{p.slug}</p>
                        <p className="text-xs text-foreground-500 mt-1">累計販売数: {soldCountByName.get(p.name) ?? 0}個</p>
                      </div>
                      <label className="flex items-center gap-2 text-xs text-foreground-600">
                        <input
                          type="checkbox"
                          checked={edit.published}
                          onChange={e => handleFieldChange(p.slug, 'published', e.target.checked)}
                        />
                        公開する
                      </label>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-xs text-foreground-500 mb-1">カテゴリ</label>
                        <input
                          type="text"
                          value={edit.category}
                          onChange={e => handleFieldChange(p.slug, 'category', e.target.value)}
                          className="w-full border border-background-300 px-3 py-2 text-sm rounded-lg outline-none focus:border-primary-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-foreground-500 mb-1">価格(円)</label>
                        <input
                          type="number"
                          value={edit.price}
                          onChange={e => handleFieldChange(p.slug, 'price', e.target.value)}
                          className="w-full border border-background-300 px-3 py-2 text-sm rounded-lg outline-none focus:border-primary-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-foreground-500 mb-1">在庫数</label>
                        <input
                          type="number"
                          value={edit.stock}
                          onChange={e => handleFieldChange(p.slug, 'stock', e.target.value)}
                          className="w-full border border-background-300 px-3 py-2 text-sm rounded-lg outline-none focus:border-primary-400"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-xs text-foreground-500 mb-1">説明文</label>
                      <textarea
                        value={edit.description}
                        onChange={e => handleFieldChange(p.slug, 'description', e.target.value)}
                        rows={2}
                        className="w-full border border-background-300 px-3 py-2 text-sm rounded-lg outline-none focus:border-primary-400 resize-none"
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleSave(p.slug)}
                        disabled={savingSlug === p.slug}
                        className="bg-primary-500 text-white px-6 py-2 text-sm rounded-full disabled:opacity-50"
                      >
                        {savingSlug === p.slug ? '保存中...' : '保存'}
                      </button>
                      {rowMessage[p.slug] && (
                        <span className="text-xs text-foreground-500">{rowMessage[p.slug]}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {tab === 'orders' && (
          <>
            {ordersError && <p className="text-sm text-primary-600 mb-4">{ordersError}</p>}

            <div className="mb-6">
              <button
                onClick={() => setShowManualForm(v => !v)}
                className="text-xs text-primary-600 hover:text-primary-700 underline"
              >
                {showManualForm ? '閉じる' : '+ 手入力で売上を追加(対面販売など)'}
              </button>
            </div>

            {showManualForm && (
              <form onSubmit={handleManualSubmit} className="bg-white border border-background-200 rounded-xl p-5 mb-8">
                <p className="text-sm text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                  手入力で売上を追加
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  {products.map(p => (
                    <div key={p.slug} className="flex items-center justify-between gap-3 border border-background-200 rounded-lg px-3 py-2">
                      <span className="text-xs text-foreground-700">{p.name}(¥{p.price.toLocaleString()})</span>
                      <input
                        type="number"
                        min={0}
                        placeholder="0"
                        value={manualQuantities[p.slug] ?? ''}
                        onChange={e => handleManualQuantityChange(p.slug, e.target.value)}
                        className="w-16 border border-background-300 px-2 py-1 text-sm rounded-lg outline-none focus:border-primary-400"
                      />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs text-foreground-500 mb-1">支払い方法</label>
                    <select
                      value={manualPaymentMethod}
                      onChange={e => setManualPaymentMethod(e.target.value)}
                      className="w-full border border-background-300 px-3 py-2 text-sm rounded-lg outline-none focus:border-primary-400"
                    >
                      <option value="現金">現金</option>
                      <option value="銀行振込">銀行振込</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-foreground-500 mb-1">お客様名(任意)</label>
                    <input
                      type="text"
                      value={manualCustomerName}
                      onChange={e => setManualCustomerName(e.target.value)}
                      className="w-full border border-background-300 px-3 py-2 text-sm rounded-lg outline-none focus:border-primary-400"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-xs text-foreground-500 mb-1">メモ(任意)</label>
                  <input
                    type="text"
                    value={manualNotes}
                    onChange={e => setManualNotes(e.target.value)}
                    className="w-full border border-background-300 px-3 py-2 text-sm rounded-lg outline-none focus:border-primary-400"
                  />
                </div>
                {manualError && <p className="text-xs text-primary-600 mb-3">{manualError}</p>}
                {manualSuccess && <p className="text-xs text-accent-600 mb-3">{manualSuccess}</p>}
                <button
                  type="submit"
                  disabled={manualSaving}
                  className="bg-primary-500 text-white px-6 py-2 text-sm rounded-full disabled:opacity-50"
                >
                  {manualSaving ? '登録中...' : '売上として登録'}
                </button>
              </form>
            )}

            {summary && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="bg-white border border-background-200 rounded-xl p-5">
                  <p className="text-xs text-foreground-500 mb-1">売上合計(決済済み)</p>
                  <p className="text-2xl text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                    ¥{summary.totalRevenue.toLocaleString()}
                  </p>
                </div>
                <div className="bg-white border border-background-200 rounded-xl p-5">
                  <p className="text-xs text-foreground-500 mb-1">決済済み件数</p>
                  <p className="text-2xl text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                    {summary.paidOrders}
                  </p>
                </div>
                <div className="bg-white border border-background-200 rounded-xl p-5">
                  <p className="text-xs text-foreground-500 mb-1">総注文件数</p>
                  <p className="text-2xl text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                    {summary.totalOrders}
                  </p>
                </div>
              </div>
            )}

            {monthlyStats.length > 0 && (
              <div className="bg-white border border-background-200 rounded-xl p-5 mb-8">
                <button
                  onClick={() => setShowMonthlyStats(v => !v)}
                  className="w-full flex items-center justify-between cursor-pointer"
                >
                  <p className="text-sm text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                    月別売上
                  </p>
                  <i className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform ${showMonthlyStats ? 'rotate-180' : ''}`}></i>
                </button>

                {showMonthlyStats && (
                  <div className="mt-5">
                    <div className="flex items-center justify-end flex-wrap gap-3 mb-5">
                      <select
                        value={activeMonth}
                        onChange={e => setSelectedMonth(e.target.value)}
                        className="text-xs border border-background-300 px-3 py-2 rounded-lg outline-none focus:border-primary-400"
                      >
                        {monthlyStats.map(m => (
                          <option key={m.month} value={m.month}>{m.month}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => window.print()}
                        className="text-xs bg-primary-500 text-white px-4 py-2 rounded-full"
                      >
                        PDFを出力
                      </button>
                    </div>

                    {activeMonthStat && (
                      <>
                        <div className="grid grid-cols-2 gap-4 mb-6">
                          <div className="bg-background-50 rounded-lg p-4">
                            <p className="text-xs text-foreground-500 mb-1">{activeMonthStat.month} 売上合計</p>
                            <p className="text-xl text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                              ¥{activeMonthStat.totalRevenue.toLocaleString()}
                            </p>
                          </div>
                          <div className="bg-background-50 rounded-lg p-4">
                            <p className="text-xs text-foreground-500 mb-1">{activeMonthStat.month} 注文件数</p>
                            <p className="text-xl text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                              {activeMonthStat.orderCount}件
                            </p>
                          </div>
                        </div>

                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-foreground-500 border-b border-background-200">
                              <th className="pb-2 font-normal">商品名</th>
                              <th className="pb-2 font-normal text-right">数量</th>
                              <th className="pb-2 font-normal text-right">売上</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeMonthStat.products.map(p => (
                              <tr key={p.name} className="border-b border-background-100">
                                <td className="py-2 text-foreground-800">{p.name}</td>
                                <td className="py-2 text-right text-foreground-800">{p.quantity}</td>
                                <td className="py-2 text-right text-foreground-950">¥{p.revenue.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* PDF出力用の印刷専用レイアウト(画面には表示されず、印刷/PDF保存時のみ表示される) */}
            {activeMonthStat && (
              <div id="sales-print-area" className="hidden print:block p-10">
                <h1 className="text-2xl mb-1">ROFF 売上報告書</h1>
                <p className="text-sm text-gray-600 mb-6">対象月: {activeMonthStat.month}</p>
                <div className="mb-6">
                  <p>売上合計: ¥{activeMonthStat.totalRevenue.toLocaleString()}</p>
                  <p>注文件数: {activeMonthStat.orderCount}件</p>
                </div>
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left border-b border-black py-2">商品名</th>
                      <th className="text-right border-b border-black py-2">数量</th>
                      <th className="text-right border-b border-black py-2">売上</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeMonthStat.products.map(p => (
                      <tr key={p.name}>
                        <td className="py-1.5">{p.name}</td>
                        <td className="py-1.5 text-right">{p.quantity}</td>
                        <td className="py-1.5 text-right">¥{p.revenue.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-gray-500 mt-8">出力日: {new Date().toLocaleDateString('ja-JP')}</p>
              </div>
            )}

            {orders.length === 0 ? (
              <p className="text-sm text-foreground-500">まだ注文がありません。</p>
            ) : (
              <div className="space-y-3">
                {orders.map(o => (
                  <div key={o.id} className="bg-white border border-background-200 rounded-xl overflow-hidden">
                    <div className="w-full flex items-center justify-between p-4 flex-wrap gap-2">
                      <button
                        onClick={() => setExpandedOrder(expandedOrder === o.id ? null : o.id)}
                        className="flex items-center gap-4 flex-wrap text-left"
                      >
                        <span className="text-sm text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                          {o.orderNumber}
                        </span>
                        <span className="text-xs text-foreground-500">{o.createdAt}</span>
                        <span className="text-xs text-foreground-700">{o.lastName} {o.firstName}様</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600">
                          {PAYMENT_PROVIDER_LABELS[o.paymentProvider] ?? o.paymentProvider}
                        </span>
                      </button>
                      <div className="flex items-center gap-3">
                        <select
                          value={o.status}
                          disabled={statusSaving === o.id}
                          onChange={e => handleStatusChange(o.id, e.target.value, o)}
                          onClick={e => e.stopPropagation()}
                          className={`text-xs px-2 py-1 rounded-full border-none outline-none ${o.status === 'paid' ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-600'}`}
                        >
                          {ORDER_STATUS_OPTIONS.map(s => (
                            <option key={s} value={s}>{STATUS_LABELS[s] ?? s}</option>
                          ))}
                        </select>
                        <span className="text-sm text-foreground-950 whitespace-nowrap" style={{ fontFamily: "var(--font-heading)" }}>
                          ¥{o.total.toLocaleString()}
                        </span>
                        <button
                          onClick={e => { e.stopPropagation(); handleDeleteOrder(o); }}
                          disabled={deletingOrder === o.id}
                          className="text-xs text-foreground-400 hover:text-primary-600 disabled:opacity-50"
                        >
                          {deletingOrder === o.id ? '削除中...' : '削除'}
                        </button>
                      </div>
                    </div>
                    {expandedOrder === o.id && (
                      <div className="border-t border-background-200 p-4 text-sm text-foreground-700 space-y-3">
                        <div>
                          <p className="text-xs text-foreground-500 mb-1">お支払い情報</p>
                          <p>{PAYMENT_PROVIDER_LABELS[o.paymentProvider] ?? o.paymentProvider}{o.paidAt ? `(支払い日時: ${o.paidAt})` : ''}</p>
                          {o.univapayChargeId && (
                            <p className="text-xs text-foreground-400">UnivaPay課金ID: {o.univapayChargeId}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500 mb-1">配送方法・追跡番号</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600">
                              {SHIPPING_METHOD_LABELS[o.shippingMethod] ?? o.shippingMethod}
                            </span>
                            <input
                              type="text"
                              placeholder="追跡番号を入力"
                              value={trackingInputs[o.id] ?? o.trackingNumber ?? ''}
                              onChange={e => setTrackingInputs(prev => ({ ...prev, [o.id]: e.target.value }))}
                              className="text-xs px-2 py-1 border border-background-300 rounded-lg w-48 outline-none focus:border-primary-400"
                            />
                          </div>
                          <p className="text-xs text-foreground-400 mt-1">
                            入力後にステータスを「発送済み」に変更すると、追跡番号入りの発送完了メールが送信されます。
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500 mb-1">配送先</p>
                          <p>{o.lastName} {o.firstName} 様</p>
                          <p>〒{o.zip} {o.prefecture}{o.city}{o.address} {o.apt}</p>
                          <p>{o.email} / {o.phone}</p>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500 mb-1">商品明細</p>
                          <ul className="space-y-1">
                            {o.items.map((item, i) => (
                              <li key={i} className="flex justify-between">
                                <span>{item.name} × {item.quantity}</span>
                                <span>¥{item.subtotal.toLocaleString()}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-background-100">
                          <span>小計 ¥{o.subtotal.toLocaleString()} + 送料({SHIPPING_METHOD_LABELS[o.shippingMethod] ?? o.shippingMethod}) ¥{o.shippingFee.toLocaleString()}</span>
                          <span style={{ fontFamily: "var(--font-heading)" }}>合計 ¥{o.total.toLocaleString()}</span>
                        </div>
                        {o.notes && <p className="text-xs text-foreground-500">備考: {o.notes}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
